import { __decorate } from "tslib";
import { Component, Mixins } from 'vue-property-decorator';
import ActionButton from '@/components/action_button/Index.vue';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import ButtonLoaderMixin from '@/helpers/vue_mixins/ButtonLoaderMixin';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import EcommerceProductBatchSave from '@/plugins/graphql/mutations/v2/ecommerce/product-batch-save.gql';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import _ from 'lodash';
import BaseCsvImporter from '@/components/v2/BaseCsvImporter.vue';
import { ProductCategory } from '@/helpers/entities/enums/v2/ProductCategory';
import EcommerceAppProducts from '@/plugins/graphql/queries/v2/ecommerce/products.gql';
import AppDetailsMixin from '@/helpers/vue_mixins/v2/ecommerce/AppDetailsMixin';
let ProductImport = class ProductImport extends Mixins(ViewportSizeMixin, ButtonLoaderMixin, AlertMixin, RouterParamsAppMixin, AppDetailsMixin) {
    constructor() {
        super(...arguments);
        this.csvData = null;
    }
    async getCsvData(data) {
        const defaultHeader = [
            'productId',
            'published',
            'productType',
            'productName',
            'productDescription',
            'stockQuantity',
            'productCategories',
            'basePrice',
            'length (in.)',
            'width (in.)',
            'height (in.)',
            'weight (kg)',
            'optionName',
            'optionId',
            'optionValues',
            'optionValueIds',
            'optionValueStockQuantities',
            'optionValuePublishes',
            'variantTypes',
            'variantPrices',
            'promoActive',
            'promoDiscount',
            'promoDiscountType',
            'promoApplicablePayment',
            'promoProducts',
        ];
        const csvHeader = Object.keys(data[0]);
        const checkHeader = _.isEqual(csvHeader.sort(), defaultHeader.sort());
        let validCsv = true;
        let errMsg = '';
        if (!checkHeader) {
            this.errorNotification('Csv fields must follow export csv fields');
        }
        else {
            if (data.length < 1) {
                validCsv = false;
                errMsg = 'No valid inputs';
            }
            const parsedData = [];
            data.every((datum) => {
                const published = _.isEmpty(datum.published)
                    ? true
                    : this.parseJSON(datum.published.toLowerCase());
                const quantity = datum.stockQuantity;
                const parsedQuantity = this.parseJSON(quantity);
                if (!_.isBoolean(published)) {
                    validCsv = false;
                    errMsg = 'Invalid publish status';
                    return false;
                }
                if (!_.isEmpty(quantity) && !_.isNumber(parsedQuantity)) {
                    validCsv = false;
                    errMsg = 'Invalid stock quantity';
                    return false;
                }
                if (!_.isEmpty(quantity) && parsedQuantity < 0) {
                    validCsv = false;
                    errMsg = 'Stock quantity could not be negative';
                    return false;
                }
                const price = datum.basePrice;
                const parsedPrice = this.parseJSON(price);
                if (!parsedPrice || !_.isNumber(parsedPrice) || parsedPrice < 0) {
                    validCsv = false;
                    errMsg = 'Invalid base price';
                    return false;
                }
                const limited = !_.isEmpty(quantity);
                const enableVariances = !!datum.optionName;
                const categories = [];
                const productCategories = datum.productCategories
                    .split(',')
                    .filter((v, i, a) => a.indexOf(v) === i);
                Object.values(productCategories).forEach((val) => {
                    categories.push({ name: val });
                });
                const productType = datum.productType || 'physical';
                if (productType !== 'physical' && productType !== 'digital') {
                    validCsv = false;
                    errMsg = 'Invalid product type';
                    return false;
                }
                const digital = productType === 'digital';
                const input = {
                    uuid: datum.productId,
                    published,
                    digitalProduct: digital,
                    name: datum.productName,
                    description: datum.productDescription,
                    category: ProductCategory.sale,
                    limited,
                    stockQuantity: _.isEmpty(quantity) ? null : parsedQuantity,
                    price: parsedPrice,
                    categories,
                    enableVariances,
                    enableDependentStock: false,
                };
                const length = datum['length (in.)'];
                const parsedLength = this.parseJSON(length);
                const width = datum['width (in.)'];
                const parsedWidth = this.parseJSON(width);
                const height = datum['height (in.)'];
                const parsedHeight = this.parseJSON(height);
                const weight = datum['weight (kg)'];
                const parsedWeight = this.parseJSON(weight);
                if ((!_.isEmpty(length) && this.validateMeasurement(parsedLength, 84)) ||
                    (!_.isEmpty(width) && this.validateMeasurement(parsedWidth, 48)) ||
                    (!_.isEmpty(height) && this.validateMeasurement(parsedHeight, 40.8)) ||
                    (!_.isEmpty(weight) && this.validateMeasurement(parsedWeight, 600))) {
                    validCsv = false;
                    errMsg = 'Product length/height/width/weight is invalid';
                    return false;
                }
                if (this.withLalamoveShipping &&
                    !digital &&
                    (_.isEmpty(length) || _.isEmpty(width) || _.isEmpty(height) || _.isEmpty(weight))) {
                    validCsv = false;
                    errMsg = 'Product length/height/width/weight is required';
                    return false;
                }
                const dimensionInput = {
                    length: parsedLength,
                    width: parsedWidth,
                    height: parsedHeight,
                    weight: parsedWeight,
                };
                Object.assign(input, dimensionInput);
                if (datum.optionName) {
                    const productOptions = [];
                    const optionValues = datum.optionValues.split(',');
                    const optionValueIds = datum.optionValueIds.split(',');
                    const variantPrices = datum.variantPrices.split(',');
                    const optionValueStockQuantities = datum.optionValueStockQuantities.split(',');
                    const optionValuePublishes = datum.optionValuePublishes.split(',');
                    const variantProductTypes = datum.variantTypes.split(',');
                    if (optionValues.length === 1 && _.isEmpty(optionValues[0])) {
                        validCsv = false;
                        errMsg = 'Invalid option values';
                        return false;
                    }
                    for (let index = 0; index < optionValues.length; index++) {
                        const optionStockQuantity = optionValueStockQuantities[index];
                        const optionLimited = !_.isEmpty(optionStockQuantity);
                        const optionPublished = _.isEmpty(optionValuePublishes[index])
                            ? true
                            : this.parseJSON(optionValuePublishes[index].toLowerCase());
                        const variantProductType = variantProductTypes[index] || 'physical';
                        if (!_.isBoolean(optionPublished)) {
                            validCsv = false;
                            errMsg = 'Invalid variant publish status';
                            return false;
                        }
                        if (!_.isEmpty(optionStockQuantity) &&
                            !_.isNumber(this.parseJSON(optionStockQuantity))) {
                            validCsv = false;
                            errMsg = 'Invalid variant stock quantity';
                            return false;
                        }
                        if (!_.isEmpty(optionStockQuantity) && this.parseJSON(optionStockQuantity) < 0) {
                            validCsv = false;
                            errMsg = 'Variant stock quantity could not be negative';
                            return false;
                        }
                        const variantPrice = variantPrices[index];
                        const parsedVariantPrice = this.parseJSON(variantPrice) || 0;
                        if (!_.isNumber(parsedVariantPrice) || parsedVariantPrice < 0) {
                            validCsv = false;
                            errMsg = 'Invalid variant price';
                            return false;
                        }
                        if (variantProductType !== 'physical' && variantProductType !== 'digital') {
                            validCsv = false;
                            errMsg = 'Invalid variant product type';
                            return false;
                        }
                        const variantDigital = variantProductType === 'digital';
                        productOptions.push({
                            uuid: optionValueIds[index],
                            name: optionValues[index],
                            limited: optionLimited,
                            stockQuantity: optionLimited ? this.parseJSON(optionStockQuantity) : null,
                            published: optionPublished,
                            price: parsedVariantPrice || 0,
                            digitalProduct: variantDigital,
                        });
                    }
                    const section = {
                        uuid: datum.optionId,
                        name: datum.optionName,
                        published: this.parseJSON(datum.published) || true,
                        productOptions,
                    };
                    const variantTotalStock = section.productOptions.reduce((n, option) => n + (option.limited ? option.stockQuantity : 0), 0);
                    if (limited && parsedQuantity < variantTotalStock) {
                        validCsv = false;
                        errMsg = 'Stock quantity could not be less than sold count';
                        return false;
                    }
                    Object.assign(input, { productSections: [section] });
                }
                if (!_.isEmpty(datum.promoDiscount) ||
                    !_.isEmpty(datum.promoActive) ||
                    !_.isEmpty(datum.promoDiscountType) ||
                    !_.isEmpty(datum.promoApplicablePayment) ||
                    !_.isEmpty(datum.promoProducts)) {
                    if (_.isEmpty(datum.promoDiscount)) {
                        validCsv = false;
                        errMsg = 'Discount value is required';
                        return false;
                    }
                    const active = this.parseJSON(datum.promoActive.toLowerCase() || 'true');
                    if (!_.isBoolean(active)) {
                        validCsv = false;
                        errMsg = 'Promo Active invalid input (true/false)';
                        return false;
                    }
                    const discount = this.parseJSON(datum.promoDiscount);
                    if (!_.isNumber(discount)) {
                        validCsv = false;
                        errMsg = 'Promo Discount must be number';
                        return false;
                    }
                    if (discount < 0) {
                        validCsv = false;
                        errMsg = 'Invalid discount value';
                        return false;
                    }
                    const discountType = (datum.promoDiscountType === 'absolute' ? 'absolute_value' : datum.promoDiscountType) ||
                        'absolute_value';
                    if (this.parseJSON(discountType)) {
                        validCsv = false;
                        errMsg = 'Promo Discount Type must be string';
                        return false;
                    }
                    if (discountType !== 'absolute_value' && discountType !== 'percentage') {
                        validCsv = false;
                        errMsg = 'Promo Discount Type invalid input (absolute/percentage)';
                        return false;
                    }
                    if (discountType === 'absolute_value' && discount > parsedPrice) {
                        validCsv = false;
                        errMsg = 'Discount value could not be greater than the base price';
                        return false;
                    }
                    if (discountType === 'percentage' && discount > 100) {
                        validCsv = false;
                        errMsg = 'Discount value could not be greater than the 100';
                        return false;
                    }
                    const applicablePaymentOption = (datum.promoApplicablePayment === 'all' ? 'to_all' : datum.promoApplicablePayment) ||
                        'to_all';
                    const validPaymentOption = ['to_all', 'bpi', 'cod', 'gcash', 'credit_card', 'bank'];
                    if (!validPaymentOption.includes(applicablePaymentOption)) {
                        validCsv = false;
                        errMsg = 'Promo Payment Option invalid input (all/bpi/cod/gcash/credit_card/bank)';
                        return false;
                    }
                    const promoInput = {
                        active,
                        discount,
                        discountType,
                        applicablePaymentOption,
                        freebies: datum.promoProducts.split(','),
                    };
                    Object.assign(input, {
                        promoParams: promoInput,
                    });
                }
                parsedData.push(input);
                return true;
            });
            if (!validCsv) {
                this.errorNotification(errMsg);
            }
            else {
                this.requestOnFlight();
                await this.save(parsedData)
                    .then(() => {
                    this.successNotification(`${parsedData.length} Products Successfully Saved`);
                })
                    .catch((e) => {
                    this.errorNotification(e.message);
                })
                    .finally(() => {
                    this.requestDone();
                });
            }
        }
    }
    async save(data) {
        return this.$apollo.mutate({
            mutation: EcommerceProductBatchSave,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    inputs: data,
                },
            },
            refetchQueries: [
                {
                    query: EcommerceAppProducts,
                    variables: {
                        appUUID: this.appUUID,
                    },
                },
            ],
        });
    }
    validateMeasurement(parsedObject, limit) {
        return !parsedObject || !_.isNumber(parsedObject) || parsedObject < 0 || parsedObject > limit;
    }
    parseJSON(text) {
        try {
            return JSON.parse(text);
        }
        catch {
            return null;
        }
    }
};
ProductImport = __decorate([
    Component({
        components: {
            ActionButton,
            BaseCsvImporter,
        },
    })
], ProductImport);
export default ProductImport;
